import { createFeatureSelector } from '@ngrx/store';
import { mapValues, orderBy } from 'lodash-es';

import { SimilarParagraphState } from './similar-paragraph.state';
import { SimilarParagraphViewModel } from '../../features/similar-paragraph/models/similar-paragraph.view-model';
import { createReferenceMemoizedSelector } from '../create-memoized-selector';

export namespace SimilarParagraphsSelectors {
  const PREFIX = 'SimilarParagraphsSelectors';
  const selectFeature = createFeatureSelector<SimilarParagraphState>('similarParagraph');

  export const similarParagraphs = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => orderBy(state.similarParagraphs, state.sort.active, state.sort.direction as 'asc' | 'desc'),
    `${PREFIX}.similarParagraphs`,
  );
  /** Each expanded deal's loaded matches beyond the best one already listed, most similar first. */
  export const extraMatches = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState): Record<string, SimilarParagraphViewModel[]> => {
      const listed = new Set((state.similarParagraphs ?? []).map((paragraph) => paragraph.paragraphId));

      return mapValues(state.expandedDeals, (expansion) =>
        orderBy(
          expansion.paragraphs.filter((paragraph) => !listed.has(paragraph.paragraphId)),
          'score',
          'desc',
        ),
      );
    },
    `${PREFIX}.extraMatches`,
  );
  /** Deals with at least one match in the last search. */
  export const matchedDealCount = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => new Set((state.similarParagraphs ?? []).map((paragraph) => paragraph.transactionId)).size,
    `${PREFIX}.matchedDealCount`,
  );
  export const searchedDealCount = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.searchedDealCount,
    `${PREFIX}.searchedDealCount`,
  );
  export const expandedDeals = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.expandedDeals,
    `${PREFIX}.expandedDeals`,
  );
  export const loading = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.loading,
    `${PREFIX}.loading`,
  );
  export const error = createReferenceMemoizedSelector(selectFeature, (state: SimilarParagraphState) => state.error, `${PREFIX}.error`);
  export const originalParagraph = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.originalParagraph,
    `${PREFIX}.originalParagraph`,
  );
  export const sort = createReferenceMemoizedSelector(selectFeature, (state: SimilarParagraphState) => state.sort, `${PREFIX}.sort`);
  export const showDiff = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.showDiff,
    `${PREFIX}.showDiff`,
  );
  export const showBaseline = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.showBaseline,
    `${PREFIX}.showBaseline`,
  );
  export const groupByDeal = createReferenceMemoizedSelector(
    selectFeature,
    (state: SimilarParagraphState) => state.groupByDeal,
    `${PREFIX}.groupByDeal`,
  );
}
